import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const Gingivectomy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Gingivectomy Fort Worth TX | Gum Contouring Surgery | Fort Worth Dental</title>
        <meta name="description" content="Gingivectomy and gum contouring surgery in Fort Worth TX. Remove excess gum tissue, treat gummy smile. Expert periodontal care. Call (817) 985-3337." />
        <meta name="keywords" content="gingivectomy fort worth, gum surgery fort worth tx, gum contouring, gummy smile treatment, gum reshaping" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/dentist-examining.jpg)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Gingivectomy in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Gum Recontouring for a Healthier, More Attractive Smile</p>
            <p className="text-sm mt-4 opacity-90">Gum Removal Surgery | Periodontal Treatment | Gummy Smile Correction</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* What Is A Gingivectomy */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/gingivectomy-new-1.jpg" 
                  alt="Gingivectomy gum surgery Fort Worth TX - Excess gum tissue removal for healthier smile and improved appearance" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examining.jpg'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">What Is A Gingivectomy?</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  A gingivectomy is a dental procedure that removes gum tissue (gingiva) from in and around a tooth or teeth. This periodontal surgery is performed by a dentist or periodontist to treat gum disease or to improve the appearance of the gums for cosmetic reasons.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  The procedure is typically performed when gum disease has caused deep pockets to form between the teeth and gums. These pockets can harbor bacteria and make proper oral hygiene difficult. A gingivectomy removes the diseased tissue and reshapes the gums to eliminate these pockets.
                </p>
              </div>
            </div>
          </section>

          {/* What Is The Procedure */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  What Is The Procedure For a Gingivectomy?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  Before the procedure begins, your Fort Worth dentist will numb the area with a local anesthetic. Once the area is numb, the excess or diseased gum tissue will be carefully removed using a scalpel, laser, or electrosurgery. The remaining gum tissue is then shaped and contoured for optimal health and appearance.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  The use of laser technology has made gingivectomy procedures more precise and comfortable, with less bleeding and faster healing times. After the procedure, a protective dressing may be placed over the area to protect it during healing.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/gingivectomy-new-2.jpg" 
                  alt="Gingivectomy procedure steps at Fort Worth Dental - Before and after gum reshaping surgery results" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* Post-Gingivectomy Care */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Post-Gingivectomy Care: Healing and Recovery</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              After your gingivectomy, proper care is essential for optimal healing:
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Follow all post-operative instructions provided by your dentist</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Avoid brushing or flossing the treated area for the first few days</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Rinse gently with warm salt water or prescribed mouthwash</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Stick to soft foods for the first week</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Take any prescribed pain medication as directed</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Avoid smoking and alcohol during the healing period</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Attend all follow-up appointments</span>
              </li>
            </ul>
            <p className="text-gray-700 text-lg leading-relaxed mt-6">
              Most patients experience complete healing within 2-4 weeks. Your gums should appear pink and healthy, with no deep pockets remaining.
            </p>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Gingivectomy?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              Our experienced periodontal team uses the latest laser technology for comfortable, effective treatment.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Laser Technology</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Schedule Your Gingivectomy Consultation</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental to learn more about gingivectomy in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Gingivectomy Appointment in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default Gingivectomy;